import i18n from "i18next";
import { useState, useEffect, useRef, useCallback } from "react";
import { useEditorStore } from "@/store/editorStore";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/lilia-ui/dialog";
import { Button } from "@/components/lilia-ui/button";
import { Input } from "@/components/lilia-ui/input";
import { Textarea } from "@/components/lilia-ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/lilia-ui/select";
import {
  IconPlus,
  IconUpload,
  IconPencil,
  IconTrash,
  IconCopy,
  IconSearch,
  IconLoader2,
} from "@tabler/icons-react";
import { ENTRY_TYPES, REQUIRED_FIELDS, OPTIONAL_FIELDS, CITATION_STYLES, formatBibliographyEntry, type CitationStyleId, type BibEntry } from "@/lib/bibtex-parser";
import { useTranslation } from "@/utils/hooks/useTranslation";
import { api, type DoiLookupResult } from "@/lib/api";

interface BibliographyEntry {
  id: string;
  citeKey: string;
  entryType: string;
  fields: Record<string, string>;
  rawBibtex?: string | null;
}

interface BibliographyPanelProps {
  documentId: string;
  entries: BibliographyEntry[];
  onAdd: (entry: Omit<BibliographyEntry, "id">) => Promise<void>;
  onUpdate: (id: string, entry: Partial<BibliographyEntry>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onImport: (bibtex: string, overwrite: boolean) => Promise<{ imported: number; updated: number; skipped: number }>;
  onInsertCitation?: (citeKey: string) => void;
}

export function BibliographyPanel({
  documentId,
  entries,
  onAdd,
  onUpdate,
  onDelete,
  onImport,
  onInsertCitation,
}: BibliographyPanelProps) {
  const { t } = useTranslation('panels');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState<BibliographyEntry | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Citation highlight (click-to-scroll from editor)
  const highlightedCiteKey = useEditorStore((s) => s.highlightedCiteKey);
  const setHighlightedCiteKey = useEditorStore((s) => s.setHighlightedCiteKey);
  const [flashingCiteKey, setFlashingCiteKey] = useState<string | null>(null);
  const entryRefsMap = useRef<Map<string, HTMLLIElement>>(new Map());

  useEffect(() => {
    if (!highlightedCiteKey) return;
    setFlashingCiteKey(highlightedCiteKey);

    // Scroll the entry into view
    const entryEl = entryRefsMap.current.get(highlightedCiteKey);
    if (entryEl) {
      entryEl.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    // Clear highlight after 2 seconds
    const timer = setTimeout(() => {
      setFlashingCiteKey(null);
      setHighlightedCiteKey(null);
    }, 2000);

    return () => clearTimeout(timer);
  }, [highlightedCiteKey, setHighlightedCiteKey]);

  // Citation style
  const [citationStyle, setCitationStyle] = useState<CitationStyleId>("apa");

  // Quick Lookup state
  const [titleQuery, setTitleQuery] = useState("");
  const [titleResults, setTitleResults] = useState<DoiLookupResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [doiInput, setDoiInput] = useState("");
  const [isbnInput, setIsbnInput] = useState("");
  const [arxivInput, setArxivInput] = useState("");
  const [lookupLoading, setLookupLoading] = useState<"doi" | "isbn" | "arxiv" | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced title search
  const handleTitleSearch = useCallback((query: string) => {
    setTitleQuery(query);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setTitleResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await api.bibliography.searchByTitle(documentId, query);
        setTitleResults(results);
      } catch (error) {
        console.error("Title search failed:", error);
        setTitleResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 500);
  }, [documentId]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleLookup = async (type: "doi" | "isbn" | "arxiv") => {
    const value = type === "doi" ? doiInput : type === "isbn" ? isbnInput : arxivInput;
    if (!value.trim()) return;
    setLookupLoading(type);
    try {
      let result: DoiLookupResult;
      if (type === "doi") {
        result = await api.bibliography.lookupDoi(documentId, value);
      } else if (type === "isbn") {
        result = await api.bibliography.lookupIsbn(documentId, value);
      } else {
        result = await api.bibliography.lookupArxiv(documentId, value);
      }
      prefillFromLookup(result);
    } catch (error) {
      console.error(`${type.toUpperCase()} lookup failed:`, error);
    } finally {
      setLookupLoading(null);
    }
  };

  const prefillFromLookup = (result: DoiLookupResult) => {
    const fields: Record<string, string> = {};
    for (const [key, value] of Object.entries(result.data)) {
      if (typeof value === "string") {
        fields[key] = value;
      }
    }
    setFormData({
      citeKey: result.citeKey,
      entryType: result.entryType || "article",
      fields,
    });
    setEditingEntry(null);
    setShowAddDialog(true);
  };

  const handleAddFromSearch = (result: DoiLookupResult) => {
    prefillFromLookup(result);
    setTitleQuery("");
    setTitleResults([]);
  };

  // Form state for add/edit
  const [formData, setFormData] = useState({
    citeKey: "",
    entryType: "article",
    fields: {} as Record<string, string>,
  });

  // Import state
  const [importBibtex, setImportBibtex] = useState("");
  const [importOverwrite, setImportOverwrite] = useState(false);

  const resetForm = () => {
    setFormData({
      citeKey: "",
      entryType: "article",
      fields: {},
    });
  };

  const handleOpenAdd = () => {
    resetForm();
    setEditingEntry(null);
    setShowAddDialog(true);
  };

  const handleOpenEdit = (entry: BibliographyEntry) => {
    setFormData({
      citeKey: entry.citeKey,
      entryType: entry.entryType,
      fields: { ...entry.fields },
    });
    setEditingEntry(entry);
    setShowAddDialog(true);
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      if (editingEntry) {
        await onUpdate(editingEntry.id, {
          citeKey: formData.citeKey,
          entryType: formData.entryType,
          fields: formData.fields,
        });
      } else {
        await onAdd({
          citeKey: formData.citeKey,
          entryType: formData.entryType,
          fields: formData.fields,
        });
      }
      setShowAddDialog(false);
      resetForm();
    } catch (error) {
      console.error("Failed to save bibliography entry:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (entry: BibliographyEntry) => {
    if (confirm(t('bibliography.deleteConfirm', { citeKey: entry.citeKey }))) {
      setIsLoading(true);
      try {
        await onDelete(entry.id);
      } catch (error) {
        console.error("Failed to delete bibliography entry:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleImport = async () => {
    setIsLoading(true);
    try {
      const result = await onImport(importBibtex, importOverwrite);
      alert(t('bibliography.importResult', { imported: result.imported, updated: result.updated, skipped: result.skipped }));
      setShowImportDialog(false);
      setImportBibtex("");
    } catch (error) {
      console.error("Failed to import BibTeX:", error);
      alert(t('bibliography.importFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyCiteKey = (citeKey: string) => {
    navigator.clipboard.writeText(`\\cite{${citeKey}}`);
  };

  const handleFieldChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      fields: { ...prev.fields, [field]: value },
    }));
  };

  const requiredFields = REQUIRED_FIELDS[formData.entryType] || [];
  const optionalFields = OPTIONAL_FIELDS[formData.entryType] || [];

  return (
    <div className="flex flex-col h-full" data-testid="bibliography-panel">
      <div className="flex justify-between items-center px-3 py-2 border-b border-[var(--lilia-border)]">
        <h3 className="m-0 text-sm font-semibold text-foreground">{t('bibliography.title')}</h3>
        <div className="flex gap-1">
          <Button
            variant="subtle"
            size="icon-xs"
            title={t('bibliography.addEntry')}
            onClick={handleOpenAdd}
            data-testid="bibliography-add"
          >
            <IconPlus size={16} />
          </Button>
          <Button
            variant="subtle"
            size="icon-xs"
            title={t('bibliography.importBibtex')}
            onClick={() => setShowImportDialog(true)}
            data-testid="bibliography-import-open"
          >
            <IconUpload size={16} />
          </Button>
        </div>
      </div>

      {/* Quick Lookup Section */}
      <div className="px-3 py-2 border-b border-[var(--lilia-border)] space-y-2">
        {/* Title Search */}
        <div className="relative">
          <IconSearch size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={titleQuery}
            onChange={(e) => handleTitleSearch(e.currentTarget.value)}
            placeholder={i18n.t("editor:bib.searchPapersByTitle")}
            data-testid="bibliography-search-input"
            className="pl-7 h-8 text-xs"
          />
          {isSearching && (
            <IconLoader2 size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground animate-spin" />
          )}
        </div>
        {titleResults.length > 0 && (
          <ul className="list-none m-0 p-0 max-h-[200px] overflow-y-auto rounded border border-[var(--lilia-border)] bg-background">
            {titleResults.map((result, idx) => {
              const title = (result.data.title as string) || "Untitled";
              const author = (result.data.author as string) || "";
              const year = (result.data.year as string) || "";
              const journal = (result.data.journal as string) || "";
              const firstAuthor = author.split(" and ")[0];
              return (
                <li
                  key={`${result.citeKey}-${idx}`}
                  className="px-2.5 py-2 text-xs border-b border-[var(--lilia-border)] last:border-b-0 hover:bg-accent cursor-pointer"
                  onClick={() => handleAddFromSearch(result)}
                  data-testid="bibliography-search-result"
                  data-cite-key={result.citeKey}
                >
                  <div className="font-medium leading-tight mb-0.5">{title}</div>
                  <div className="text-muted-foreground flex items-center gap-1.5">
                    {firstAuthor && <span className="truncate max-w-[120px]">{firstAuthor}</span>}
                    {year && <span>{year}</span>}
                    {journal && <span className="truncate max-w-[100px] italic">{journal}</span>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* DOI / ISBN / arXiv lookup inputs */}
        <div className="grid grid-cols-[1fr_auto] gap-1.5 items-center">
          <Input
            value={doiInput}
            onChange={(e) => setDoiInput(e.currentTarget.value)}
            placeholder="DOI (e.g. 10.1000/xyz)"
            data-testid="bibliography-doi-input"
            className="h-7 text-xs"
          />
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs px-2"
            onClick={() => handleLookup("doi")}
            data-testid="bibliography-doi-lookup"
            disabled={lookupLoading === "doi" || !doiInput.trim()}
          >
            {lookupLoading === "doi" ? <IconLoader2 size={12} className="animate-spin" /> : "DOI"}
          </Button>
        </div>
        <div className="grid grid-cols-[1fr_auto] gap-1.5 items-center">
          <Input
            value={isbnInput}
            onChange={(e) => setIsbnInput(e.currentTarget.value)}
            placeholder="ISBN (e.g. 978-3-16-148410-0)"
            data-testid="bibliography-isbn-input"
            className="h-7 text-xs"
          />
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs px-2"
            onClick={() => handleLookup("isbn")}
            data-testid="bibliography-isbn-lookup"
            disabled={lookupLoading === "isbn" || !isbnInput.trim()}
          >
            {lookupLoading === "isbn" ? <IconLoader2 size={12} className="animate-spin" /> : "ISBN"}
          </Button>
        </div>
        <div className="grid grid-cols-[1fr_auto] gap-1.5 items-center">
          <Input
            value={arxivInput}
            onChange={(e) => setArxivInput(e.currentTarget.value)}
            placeholder="arXiv ID (e.g. 2301.07041)"
            data-testid="bibliography-arxiv-input"
            className="h-7 text-xs"
          />
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs px-2"
            onClick={() => handleLookup("arxiv")}
            data-testid="bibliography-arxiv-lookup"
            disabled={lookupLoading === "arxiv" || !arxivInput.trim()}
          >
            {lookupLoading === "arxiv" ? <IconLoader2 size={12} className="animate-spin" /> : "arXiv"}
          </Button>
        </div>
      </div>

      {/* Citation Style Selector */}
      <div className="px-3 py-2 border-b border-[var(--lilia-border)]">
        <Select value={citationStyle} onValueChange={(val) => setCitationStyle(val as CitationStyleId)}>
          <SelectTrigger className="h-7 text-xs">
            <SelectValue placeholder={i18n.t("editor:bib.citationStyle")} />
          </SelectTrigger>
          <SelectContent>
            {CITATION_STYLES.map((style) => (
              <SelectItem key={style.id} value={style.id}>
                <span className="text-xs">{style.name}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {entries.length > 0 ? (
        <ul className="list-none m-0 p-2 overflow-y-auto flex-1">
          {entries.map((entry) => {
            const bibEntry: BibEntry = {
              citeKey: entry.citeKey,
              entryType: entry.entryType,
              fields: entry.fields,
            };
            const formattedCitation = formatBibliographyEntry(bibEntry, citationStyle);
            return (
              <li
                key={entry.id}
                data-testid="bibliography-entry"
                data-cite-key={entry.citeKey}
                ref={(el) => {
                  if (el) entryRefsMap.current.set(entry.citeKey, el);
                  else entryRefsMap.current.delete(entry.citeKey);
                }}
                className={`p-2.5 mb-2 bg-muted rounded text-[13px] hover:bg-accent transition-all duration-300 ${flashingCiteKey === entry.citeKey ? "ring-2 ring-yellow-400 bg-yellow-50 dark:bg-yellow-900/20" : ""}`}
              >
                <div className="flex justify-between items-center mb-1">
                  <strong className="font-mono text-primary text-xs">{entry.citeKey}</strong>
                  <span className="text-[11px] text-muted-foreground bg-background px-1.5 py-0.5 rounded uppercase">{entry.entryType}</span>
                </div>
                <div
                  className="text-xs text-muted-foreground leading-relaxed mb-1"
                  dangerouslySetInnerHTML={{ __html: formattedCitation }}
                />
                <div className="flex gap-1 mt-2 justify-end">
                  {onInsertCitation && (
                    <Button
                      size="sm"
                      variant="subtle"
                      onClick={() => onInsertCitation(entry.citeKey)}
                      data-testid="bibliography-entry-cite"
                      title={t('bibliography.cite')}
                    >
                      {t('bibliography.cite')}
                    </Button>
                  )}
                  <Button
                    size="icon-xs"
                    variant="subtle"
                    onClick={() => handleCopyCiteKey(entry.citeKey)}
                    data-testid="bibliography-entry-copy"
                    title={t('common:copy')}
                  >
                    <IconCopy size={16} />
                  </Button>
                  <Button
                    size="icon-xs"
                    variant="subtle"
                    onClick={() => handleOpenEdit(entry)}
                    data-testid="bibliography-entry-edit"
                    title={t('common:edit')}
                  >
                    <IconPencil size={16} />
                  </Button>
                  <Button
                    size="icon-xs"
                    variant="subtle"
                    onClick={() => handleDelete(entry)}
                    data-testid="bibliography-entry-delete"
                    title={t('common:delete')}
                  >
                    <IconTrash size={16} />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-center py-5 text-muted-foreground text-[13px]">
          {t('bibliography.noEntries')}
        </p>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingEntry ? t('bibliography.editCitation') : t('bibliography.addCitation')}</DialogTitle>
          </DialogHeader>
          <div className="p-4">
            <div className="mb-3">
              <label className="block mb-1 text-[13px] font-medium text-foreground capitalize">{t('bibliography.citationKey')}</label>
              <Input
                value={formData.citeKey}
                onChange={(e) => {
                  // Read the value BEFORE the updater runs. React clears
                  // currentTarget once the handler returns, and a functional
                  // updater runs after that — so `e.currentTarget.value` in
                  // here threw "Cannot read properties of null (reading
                  // 'value')" on the first keystroke and took the whole editor
                  // down with it. That is why adding a reference through the
                  // panel was impossible.
                  const value = e.currentTarget.value;
                  setFormData((prev) => ({ ...prev, citeKey: value }));
                }}
                placeholder={t('bibliography.citationKeyPlaceholder')}
                data-testid="bibliography-citekey-input"
              />
            </div>

            <div className="mb-3">
              <label className="block mb-1 text-[13px] font-medium text-foreground capitalize">{t('bibliography.entryType')}</label>
              <Select value={formData.entryType} onValueChange={(val) =>
                setFormData((prev) => ({ ...prev, entryType: val || "article" }))
              }>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...ENTRY_TYPES].map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <h4 className="mt-4 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('bibliography.requiredFields')}</h4>
            {requiredFields.map((field) => (
              <div key={field} className="mb-3">
                <label className="block mb-1 text-[13px] font-medium text-foreground capitalize">{field}</label>
                <Input
                  value={formData.fields[field] || ""}
                  onChange={(e) => handleFieldChange(field, e.currentTarget.value)}
                />
              </div>
            ))}

            <h4 className="mt-4 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('bibliography.optionalFields')}</h4>
            {optionalFields.map((field) => (
              <div key={field} className="mb-3">
                <label className="block mb-1 text-[13px] font-medium text-foreground capitalize">{field}</label>
                <Input
                  value={formData.fields[field] || ""}
                  onChange={(e) => handleFieldChange(field, e.currentTarget.value)}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="bibliography-cancel" onClick={() => setShowAddDialog(false)}>{t('common:cancel')}</Button>
            <Button
              variant="primary"
              onClick={handleSave}
              data-testid="bibliography-save"
              disabled={!formData.citeKey || isLoading}
            >
              {isLoading ? t('common:saving') : editingEntry ? t('common:update') : t('common:add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{t('bibliography.importBibtex')}</DialogTitle>
          </DialogHeader>
          <div className="p-4">
            <p className="mb-3 text-[13px] text-muted-foreground">
              {t('bibliography.pasteBibtex')}
            </p>
            <Textarea
              value={importBibtex}
              onChange={(e) => setImportBibtex(e.currentTarget.value)}
              data-testid="bibliography-bibtex-input"
              placeholder={`@article{einstein1905,
  author = {Albert Einstein},
  title = {On the Electrodynamics of Moving Bodies},
  journal = {Annalen der Physik},
  year = {1905},
  volume = {322},
  pages = {891-921}
}`}
              rows={12}
              className="font-mono"
            />
            <div className="mt-3">
              <label className="flex items-center text-[13px] font-medium text-foreground">
                <input
                  type="checkbox"
                  checked={importOverwrite}
                  onChange={(e) => setImportOverwrite(e.target.checked)}
                  className="mr-2"
                />
                {t('bibliography.overwriteExisting')}
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="bibliography-import-cancel" onClick={() => setShowImportDialog(false)}>{t('common:cancel')}</Button>
            <Button
              variant="primary"
              onClick={handleImport}
              data-testid="bibliography-import-confirm"
              disabled={!importBibtex.trim() || isLoading}
            >
              {isLoading ? t('common:importing') : t('common:import')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
